/**
 * Remembers where an unauthenticated visitor was heading, so that after logging in
 * they land on that exact page instead of the default home.
 *
 * This is the same-browser half of the feature: the magic-link email carries the
 * same target as a `redirect` param, which covers the case where the visitor opens
 * the email on another browser or device.
 *
 * Only relative in-app paths are ever stored or returned — an absolute URL here
 * would turn the login flow into an open redirect.
 */

const STORAGE_KEY = 'magmaReturnTo';

// Long enough to leave the app, open the mailbox and click the link; short enough
// that tomorrow's login is not hijacked by a forgotten target.
const TTL_MS = 30 * 60 * 1000;

// Landing on one of these after login would either loop or be meaningless.
const EXCLUDED_PREFIXES = ['/login', '/sign-in', '/t/', '/expired-link'];

export const isSafeReturnPath = (path: unknown): path is string => {
  if (typeof path !== 'string' || !path) return false;
  if (path.length > 200) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.startsWith('/\\')) return false;
  if (path.includes('://') || path.includes('\\')) return false;
  if (EXCLUDED_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;

  return true;
};

// A stored magic token would be replayed after login (and keeps failing if it is why we left).
const withoutMagicToken = (path: string) => {
  const url = new URL(path, 'https://v3.magma.app');
  url.searchParams.delete('token');
  return `${url.pathname}${url.search}${url.hash}`;
};

export const saveReturnTo = (path: unknown) => {
  if (!isSafeReturnPath(path)) return;
  const safePath = withoutMagicToken(path);
  if (!isSafeReturnPath(safePath)) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path: safePath, exp: Date.now() + TTL_MS })
    );
  } catch {
    // Private mode / storage disabled: the emailed redirect still covers us.
  }
};

export const clearReturnTo = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to do
  }
};

const _read = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const { path, exp } = JSON.parse(raw) as { path?: string; exp?: number };
    if (!exp || exp < Date.now() || !isSafeReturnPath(path)) {
      clearReturnTo();
      return null;
    }

    return path;
  } catch {
    clearReturnTo();
    return null;
  }
};

/** Reads the target without consuming it — the login form may be resubmitted. */
export const peekReturnTo = (): string | null => _read();

/** Reads the target and removes it, so it is only ever honoured once. */
export const consumeReturnTo = (): string | null => {
  const path = _read();
  if (path) clearReturnTo();
  return path;
};
